import { Edit, LockOpen, LockOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { ExplanationAccordion, LockStatusChip, SortableTableCell } from 'components'
import { Chip } from 'components/chips'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { ToDoListNeedsSpecialBondParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import VehicleLockStatusDialog from './VehicleLockStatusDialog'
import { enqueueSnackbar } from 'notistack'

export default function SpecialBondToDoListDetails() {
  const navigate = useNavigate()
  const layout = useLayout()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false,
    isLockingVehicles: false,
    isUnlockingVehicles: false
  })

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const [vehicleLockStatusVehicle, setVehicleLockStatusVehicle] = useState<VehicleDto>(new VehicleDto())
  const [vehicleLockStatusDialogOpen, setVehicleLockStatusDialogOpen] = useState<boolean>(false)

  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const [parameters, setParameters] = useState<ToDoListNeedsSpecialBondParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'createdDateTime',
    order: 'asc'
  })

  const lockVehicles = () => {
    setLoadingState('isLockingVehicles', true)
    controllers
      .lockVehicles(selectedVehicles)
      .then(response => {
        enqueueSnackbar('Vehicles Locked Successfully!', { variant: 'success' })
        setSelectedVehicles([])
        getNeedsSpecialBondToDoList(parameters)
      })
      .finally(() => {
        setLoadingState('isLockingVehicles', false)
      })
  }

  const unlockVehicles = () => {
    setLoadingState('isUnlockingVehicles', true)
    controllers
      .unlockVehicles(selectedVehicles)
      .then(response => {
        enqueueSnackbar('Vehicles Unlocked Successfully!', { variant: 'success' })
        setSelectedVehicles([])
        getNeedsSpecialBondToDoList(parameters)
      })
      .finally(() => {
        setLoadingState('isUnlockingVehicles', false)
      })
  }

  const getNeedsSpecialBondToDoList = useDebounce((parameters: ToDoListNeedsSpecialBondParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeedsSpecialBondToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs Special Bond To Do List')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(
      loadingState.isGettingVehicles || loadingState.isLockingVehicles || loadingState.isUnlockingVehicles
    )
  }, [layout, loadingState.isGettingVehicles, loadingState.isLockingVehicles, loadingState.isUnlockingVehicles])

  useEffect(() => {
    getNeedsSpecialBondToDoList(parameters)
  }, [getNeedsSpecialBondToDoList, parameters])

  return (
    <>
      <VehicleLockStatusDialog
        onClose={() => {
          setVehicleLockStatusDialogOpen(false)
        }}
        open={vehicleLockStatusDialogOpen}
        vehicle={vehicleLockStatusVehicle}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created Date</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='vehicleType'>Vehicle Type</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    {/* <MenuItem value='buyer'>Buyer</MenuItem> */}

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='canBeLocked'>Lock Status</MenuItem>

                    <MenuItem value='isLocked'>Locked</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporter', 'vehicleType', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'createdDateTime' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'canBeLocked' && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      fullWidth
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      select
                      sx={{ minWidth: 200 }}
                      value={filterValue || ''}
                    >
                      <MenuItem value='true'>
                        <Chip variant='green'>Can Be Locked</Chip>
                      </MenuItem>

                      <MenuItem value='false'>
                        <Chip variant='red'>Incomplete</Chip>
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}

                {filterField === 'isLocked' && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      fullWidth
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      select
                      sx={{ minWidth: 200 }}
                      value={filterValue || ''}
                    >
                      <MenuItem value='true'>
                        <Chip variant='green'>Locked</Chip>
                      </MenuItem>

                      <MenuItem value='false'>
                        <Chip variant='red'>Unlocked</Chip>
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (filterField === 'createdDateTime' ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      console.log('filterField', filterField)
                      console.log('filterValue', filterValue)
                      if (filterField === 'createdDateTime') {
                        setParameters({
                          ...parameters,
                          createdDateFrom: filterValueFrom,
                          createdDateTo: filterValueTo
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.createdDateFrom ||
                          parameters.createdDateTo ||
                          parameters.exporter ||
                          parameters.vehicleType ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model ||
                          parameters.canBeLocked ||
                          parameters.isLocked
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined,
                        exporter: undefined,
                        vehicleType: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined,
                        canBeLocked: undefined,
                        isLocked: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto' container alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    color='primary'
                    disabled={selectedVehicles.length === 0}
                    endIcon={<LockOutlined />}
                    onClick={lockVehicles}
                    variant='text'
                  >
                    LOCK VEHICLE
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    disabled={selectedVehicles.length === 0}
                    endIcon={<LockOpen />}
                    onClick={unlockVehicles}
                    variant='text'
                  >
                    UNLOCK VEHICLE
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.createdDateFrom && parameters.createdDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created Date from "${moment(parameters.createdDateFrom).format('M/D/yyyy')}" to "${moment(
                      parameters.createdDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vehicleType && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Type is ${parameters.vehicleType}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleType: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains ${parameters.vin}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${parameters.year}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains ${parameters.make}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains ${parameters.model}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.canBeLocked && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Lock Status is "${parameters.canBeLocked.toString() === 'true' ? 'Can Be Locked' : 'Incomplete'}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        canBeLocked: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.isLocked && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Locked is "${parameters.isLocked.toString() === 'true' ? 'Locked' : 'Unlocked'}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        isLocked: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selectedVehicles.length === totalCount}
                                indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                                onChange={() => {
                                  if (selectedVehicles.length === totalCount) {
                                    setSelectedVehicles([])
                                  } else {
                                    setSelectedVehicles(vehicles ?? [])
                                  }
                                }}
                              />
                            </TableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='createdDateTime'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Created Date
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='exporter'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Exporter
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vehicleType'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Vehicle Type
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vin'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              VIN
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='year'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Year
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='make'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Make
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='model'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Model
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='isLocked'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Lock Status
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='isLocked'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            ></SortableTableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {vehicles?.map((vehicle, index) => (
                            <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}>
                                <Checkbox
                                  color='primary'
                                  checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                  onChange={() => {
                                    if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                      setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                    } else {
                                      setSelectedVehicles([...selectedVehicles, vehicle])
                                    }
                                  }}
                                />
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4 ? 'bold' : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.createdDateTime)}
                              </TableCell>

                              <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                              <TableCell>{vehicle.vehicleType?.name}</TableCell>

                              <TableCell>{vehicle.vin}</TableCell>

                              <TableCell>{vehicle.year}</TableCell>

                              <TableCell>{vehicle.make}</TableCell>

                              <TableCell>{vehicle.model}</TableCell>

                              <TableCell>
                                <LockStatusChip
                                  canBeLocked={vehicle.canBeLocked}
                                  isLocked={vehicle.isLocked}
                                  onClick={() => {
                                    setVehicleLockStatusVehicle(vehicle)
                                    setVehicleLockStatusDialogOpen(true)
                                  }}
                                />
                              </TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    navigate('/vehicles/' + vehicle.vehicleGuid)
                                  }}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || Vehicles appear in this to do list if the vehicle amount is greater than or equal to Bond Threshold (managed
                in Settings) and the Special Bond # input field is blank. || The Needs Special Bond “To Do List” column “Created
                Date” value will be in bold and red if current date is ≥ Created Date + 4 days. || The Needs Special Bond “To Do
                List” is a list screen intended for users on a PC. || BWI users may see all vehicles belonging to all Exporters.
                When Exporter company users sign into VTS2, they can only see vehicles belonging to their Exporter Company. ||
                When a user presses “Lock Vehicle” a check will run to see if any vehicles that are already locked are selected
                and if so, warn the user, “Please only pick vehicles that are not already locked.” || When a user presses “Lock
                Vehicle” a check will run to see if any selected vehicles have “Incomplete” in the Lock Status column and if so,
                warn the user, “Please only pick vehicles that show “Can Be Locked” in the Lock Status column.” When a user
                presses “Unlock Vehicle” a check will run to see if any vehicles that are already unlocked are selected and if
                so, warn the user, “Please only pick vehicles that are not already unlocked” and the vehicle is not unlocked. ||
                When a user presses “Unlock Vehicle” a check will run to see if any vehicles that are associated with a PAPS are
                selected and if so, warn the user, “Vehicles that are associated with a PAPS cannot be unlocked” and the vehicle
                is not unlocked. || After selecting vehicles on the To Do List, a user may press the “Lock Vehicle” button and
                when this is pressed, a check will run to verify that the selected vehicle record(s) can be locked. A vehicle
                can only be locked if the “Lock Status” column button for that vehicle says, “Can Be Locked”. Locking a vehicle
                will: || - Make it so the vehicle’s Add/Edit VIN dialog’s SAVE button cannot be pressed. || - Make it so the
                vehicle’s Vehicle Details - Exporter tab’s SAVE button cannot be pressed (users will still be able to add
                Exporter Notes). || - Assign the Importer Stock# which is a label that is a sequential number beginning with
                20000 followed by the 3 character “Exporter Code” or 3 character “Buyer Code” if there is a buyer on the vehicle
                (e.g., 20001-ABC). This field is only populated when the vehicle is locked for the first time and if a locked
                vehicle becomes unlocked, the Importer Stock# remains (the Importer Stock# cannot be changed or removed from a
                vehicle once assigned). || - Assign the Bond # if the Amount (USD) is less than Bond Threshold. The Bond # to
                assign is the next available Bond number from the current Bond Block. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
